import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { TrendingUp, Mail, Loader2, CheckCircle } from "lucide-react";
import { api } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const { t } = useTranslation();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error(t("common.required"));
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (error) {
            const status = error.response?.status;
            if (status === 429) {
                toast.error(t("auth.rateLimitError"));
            } else {
                toast.error(error.response?.data?.detail || t("errors.generic"));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2">
                        <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
                        </div>
                        <span className="font-heading font-semibold text-xl text-foreground">True Flow</span>
                    </Link>
                </div>

                <Card className="shadow-sm">
                    <CardHeader className="text-center">
                        <CardTitle className="font-heading text-2xl">{t("auth.forgotPasswordTitle")}</CardTitle>
                        <CardDescription>{t("auth.forgotPasswordDesc")}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {sent ? (
                            <div className="flex flex-col items-center gap-3 py-4 text-center">
                                <CheckCircle className="w-12 h-12 text-green-500" />
                                <p className="font-medium text-slate-800">{t("auth.emailSentTitle")}</p>
                                <p className="text-sm text-muted-foreground">{t("auth.emailSentDesc")}</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t("auth.email")}</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="tu@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="pl-10"
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <Button type="submit" className="w-full" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t("common.loading")}
                                        </>
                                    ) : (
                                        t("auth.sendResetLink")
                                    )}
                                </Button>
                            </form>
                        )}

                        <p className="text-center text-sm text-muted-foreground">
                            <Link to="/login" className="text-primary hover:underline font-medium">
                                {t("auth.backToLogin")}
                            </Link>
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
